import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import { Mail, ArrowRight, Loader as Loader2, Trophy, Mic, ScrollText, ChevronDown, ChevronUp, Heart, Play } from 'lucide-react';
import { KioskService, KioskLeaderboardResponse, KioskPlayableSubmission, getStoredKioskSession } from '../services/KioskService';
import ReactMarkdown from 'react-markdown';
import { useKioskBasePath } from '../hooks/useKioskBasePath';
import { TvFocusableInput } from '../components/TvFocusableInput';

export const KioskLeagueLanding: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const tmQuery = searchParams.get('testmode') === 'true' ? '?testmode=true' : '';
  const navigate = useNavigate();
  const basePath = useKioskBasePath();
  const [data, setData] = useState<KioskLeaderboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rulesExpanded, setRulesExpanded] = useState(false);
  const [likeCounts, setLikeCounts] = useState<Record<string, number>>({});
  const [myLikes, setMyLikes] = useState<Set<string>>(new Set());
  const [likingId, setLikingId] = useState<string | null>(null);
  const [playableMap, setPlayableMap] = useState<Record<string, KioskPlayableSubmission>>({});
  const [form, setForm] = useState({
    email: '',
    displayName: '',
    passcode: '',
    allowPlayback: false,
    marketingOptIn: false,
    agreedTerms: false,
  });

  const loadLikes = useCallback(async () => {
    if (!slug) return;
    try {
      const likes = await KioskService.getLikes(slug);
      setLikeCounts(likes.counts);
      setMyLikes(new Set(likes.my_likes));
    } catch {
      // non-critical
    }
  }, [slug]);

  const loadPlayable = useCallback(async () => {
    if (!slug) return;
    try {
      const subs = await KioskService.getPlayableSubmissions(slug);
      const map: Record<string, KioskPlayableSubmission> = {};
      for (const s of subs) map[s.participant_id] = s;
      setPlayableMap(map);
    } catch {
      // non-critical
    }
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    (async () => {
      try {
        setLoading(true);
        const d = await KioskService.getLeaderboard(slug);
        setData(d);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load league');
      } finally {
        setLoading(false);
      }
    })();
    loadLikes();
    loadPlayable();
  }, [slug, loadLikes, loadPlayable]);

  const session = getStoredKioskSession();
  const isSignedIn = !!session && session.leagueSlug === slug;

  const signupMode = data?.league?.signup_mode ?? 'email_verified';

  const handleLike = async (participantId: string) => {
    if (likingId || !isSignedIn) return;
    setLikingId(participantId);
    try {
      const result = await KioskService.toggleLike(participantId);
      setLikeCounts(prev => ({ ...prev, [participantId]: result.like_count }));
      setMyLikes(prev => {
        const next = new Set(prev);
        if (result.liked) next.add(participantId);
        else next.delete(participantId);
        return next;
      });
    } catch {
      // ignore
    } finally {
      setLikingId(null);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (signupMode !== 'passcode' && !form.agreedTerms) { setError('Please agree to the terms before continuing.'); return; }
    if (!slug) return;
    if (!form.displayName.trim()) { setError('Please enter a display name.'); return; }
    try {
      setSubmitting(true);
      if (signupMode === 'name_only') {
        await KioskService.signupNameOnly(slug, form.displayName.trim());
        navigate(`/${basePath}/${slug}/me${tmQuery}`);
        return;
      }
      if (signupMode === 'passcode') {
        if (!form.passcode.trim()) { setError('Please enter the passcode.'); return; }
        await KioskService.signupWithPasscode(slug, form.displayName.trim(), form.passcode.trim());
        navigate(`/${basePath}/${slug}/me${tmQuery}`);
        return;
      }
      if (!form.email.trim()) { setError('Please enter your email.'); return; }
      const res = await KioskService.requestVerification(slug, form.email, form.displayName, form.marketingOptIn);
      const params = new URLSearchParams({ email: form.email });
      if (res.debug_code) params.set('dc', res.debug_code);
      if (searchParams.get('testmode') === 'true') params.set('testmode', 'true');
      navigate(`/${basePath}/${slug}/verify?${params.toString()}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign up');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-300"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }
  if (error && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-300 text-center p-8">
        <div>
          <Trophy className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">League not found</h1>
          <p className="text-slate-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { league } = data;

  const leaderboardEntries = [...data.entries]
    .sort((a, b) => Number(b.score) - Number(a.score))
    .slice(0, 10);
  const bgStyle: React.CSSProperties = league.background_image_url
    ? { backgroundImage: `url(${league.background_image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: `linear-gradient(135deg, ${league.primary_color}, ${league.secondary_color})` };

  const filteredSponsors = league.sponsor_logos || [];
  const filteredSponsorLinks = league.sponsor_links || [];

  return (
    <div className="min-h-screen" style={bgStyle}>
      <div className="min-h-screen bg-black/50 backdrop-blur-sm flex flex-col">
        {/* Powered by header */}
        <div className="bg-black/60 backdrop-blur-sm border-b border-white/5">
          <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-center gap-3">
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/70 font-semibold">
              Powered by The Singing League
            </span>
            <img src="/TSL_Logo.png" alt="The Singing League" className="h-12 md:h-14 object-contain" />
          </div>
        </div>

        <div className="flex-1 max-w-6xl mx-auto px-4 py-10 md:py-16 w-full">
          <header className="text-center mb-10 text-white">
            {league.logo_url && (
              <img src={league.logo_url} alt={league.name} className="h-16 mx-auto mb-6 object-contain" />
            )}
            <h1 className="text-4xl md:text-5xl font-bold mb-3 tracking-tight drop-shadow-lg">
              {league.signup_headline || league.name}
            </h1>
            {(league.signup_subtext || league.description) && (
              <p className="text-lg text-white/90 max-w-2xl mx-auto">
                {league.signup_subtext || league.description}
              </p>
            )}
          </header>

          <div className="grid md:grid-cols-5 gap-6 items-start">
            {/* Signup card - dominant on the page */}
            <div className="md:col-span-3 space-y-4">
              {isSignedIn ? (
                <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
                  <Mic className="w-12 h-12 mx-auto mb-3" style={{ color: league.primary_color }} />
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Welcome back!</h2>
                  <p className="text-slate-600 mb-6">You're signed in as <strong>{session?.displayName}</strong>.</p>
                  <Link
                    to={`/${basePath}/${slug}/me`}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white"
                    style={{ backgroundColor: league.primary_color }}
                  >
                    Continue <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              ) : (
                <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 space-y-4">
                  <div className="text-center mb-2">
                    <h2 className="text-2xl font-bold text-slate-900">Join {league.name}</h2>
                    <p className="text-sm text-slate-600">
                      {signupMode === 'name_only'
                        ? 'Sign up in seconds with just a display name.'
                        : signupMode === 'passcode'
                        ? 'Enter the passcode and your name to join.'
                        : "Sign up in 30 seconds. We'll email you a 6-digit code."}
                    </p>
                  </div>

                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-slate-800 mb-1">Display Name</label>
                    <TvFocusableInput
                      value={form.displayName}
                      onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                      enterKeyHint="next"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900 placeholder:text-slate-400 bg-white focus:outline-none focus:ring-2"
                      required
                      style={{ outlineColor: league.primary_color }}
                    />
                    <p className="text-xs text-slate-500 mt-1">Appears on the leaderboard.</p>
                  </div>

                  {signupMode === 'email_verified' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-800 mb-1">Email</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10 pointer-events-none" />
                        <TvFocusableInput
                          type="email"
                          enterKeyHint="done"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg text-slate-900 placeholder:text-slate-400 bg-white"
                        />
                      </div>
                    </div>
                  )}

                  {signupMode === 'passcode' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-800 mb-1">Passcode</label>
                      <TvFocusableInput
                        value={form.passcode}
                        onChange={(e) => setForm({ ...form, passcode: e.target.value.toUpperCase() })}
                        enterKeyHint="done"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-slate-900 placeholder:text-slate-400 bg-white font-mono tracking-widest text-center text-lg uppercase"
                        placeholder="Enter passcode"
                        required
                      />
                    </div>
                  )}

                  {signupMode !== 'passcode' && (
                    <label className="flex items-start gap-2 text-sm text-slate-700">
                      <input type="checkbox" checked={form.allowPlayback} onChange={(e) => setForm({ ...form, allowPlayback: e.target.checked })} className="mt-1" />
                      <span>Allow my recording to be played back on the leaderboard.</span>
                    </label>
                  )}
                  {signupMode === 'email_verified' && (
                    <label className="flex items-start gap-2 text-sm text-slate-700">
                      <input type="checkbox" checked={form.marketingOptIn} onChange={(e) => setForm({ ...form, marketingOptIn: e.target.checked })} className="mt-1" />
                      <span>I'd like to hear about future events from {league.name}.</span>
                    </label>
                  )}
                  {signupMode !== 'passcode' && (
                    <label className="flex items-start gap-2 text-sm text-slate-700">
                      <input type="checkbox" checked={form.agreedTerms} onChange={(e) => setForm({ ...form, agreedTerms: e.target.checked })} className="mt-1" />
                      <span>I agree to the terms and acknowledge that my score will be publicly shown.</span>
                    </label>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-white font-semibold disabled:opacity-60"
                    style={{ backgroundColor: league.primary_color }}
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{signupMode === 'name_only' ? 'Start Singing' : 'Join the League'} <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </form>
              )}

              {/* Contest Rules - hidden for passcode (sing-along) mode */}
              {signupMode !== 'passcode' && (
                <div className="rounded-2xl overflow-hidden border border-white/10 bg-white/10 backdrop-blur">
                  <button
                    onClick={() => setRulesExpanded(!rulesExpanded)}
                    className="w-full flex items-center justify-between gap-3 px-5 py-4 text-white hover:bg-white/5 transition"
                  >
                    <span className="flex items-center gap-2 font-semibold text-sm">
                      <ScrollText className="w-4 h-4" style={{ color: league.accent_color }} />
                      Contest Rules
                    </span>
                    {rulesExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {rulesExpanded && (
                    <div className="px-5 pb-4 border-t border-white/10 pt-3">
                      {league.rules_markdown ? (
                        <div className="prose prose-sm prose-invert max-w-none [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:leading-relaxed [&_p]:text-white/90 [&_h1]:text-lg [&_h2]:text-base [&_h3]:text-sm [&_a]:text-sky-300 [&_a]:underline">
                          <ReactMarkdown>{league.rules_markdown}</ReactMarkdown>
                        </div>
                      ) : league.rules && league.rules.length > 0 ? (
                        <ol className="list-decimal list-inside space-y-2 text-sm text-white/90">
                          {league.rules.map((rule, i) => (
                            <li key={i} className="leading-relaxed">{rule}</li>
                          ))}
                        </ol>
                      ) : (
                        <p className="text-sm text-white/60 italic">No contest rules have been published yet.</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Leaderboard */}
            <aside className="md:col-span-2">
              <div className="bg-slate-900/80 backdrop-blur border border-white/10 rounded-2xl overflow-hidden text-white shadow-2xl">
                <div className="p-4 border-b border-white/10 flex items-center gap-2" style={{ backgroundColor: `${league.primary_color}30` }}>
                  <Trophy className="w-5 h-5" style={{ color: league.accent_color }} />
                  <h3 className="font-bold">Leaderboard</h3>
                </div>
                {leaderboardEntries.length === 0 ? (
                  <div className="p-6 text-center text-white/50 text-sm">
                    No scores yet. Be the first to sing!
                  </div>
                ) : (
                  <ol className="divide-y divide-white/10">
                    {leaderboardEntries.map((e) => {
                      const sub = playableMap[e.participant_id];
                      const hasPlayback = !!sub && (!!sub.mixed_audio_url || sub.mix_status === 'failed');
                      return (
                      <li
                        key={e.participant_id}
                        className="flex items-center px-4 py-2.5 transition"
                      >
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs mr-3 shrink-0 relative"
                          style={{
                            backgroundColor: e.rank === 1 ? league.accent_color : 'rgba(255,255,255,0.08)',
                            color: e.rank === 1 ? '#0f172a' : 'white',
                          }}
                        >
                          {e.rank}
                        </div>
                        {hasPlayback && (
                          <a
                            href={`/${basePath}/${slug}/share/${sub.submission_id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="w-6 h-6 rounded-full flex items-center justify-center mr-2 shrink-0 transition hover:scale-110 active:scale-95"
                            style={{ backgroundColor: league.accent_color }}
                            onClick={(ev) => ev.stopPropagation()}
                          >
                            <Play size={10} className="text-slate-900 ml-px" fill="currentColor" />
                          </a>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate text-sm">{e.display_name}</div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {isSignedIn && session?.participantId !== e.participant_id ? (
                            <button
                              onClick={(ev) => { ev.stopPropagation(); handleLike(e.participant_id); }}
                              disabled={likingId === e.participant_id}
                              className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold transition hover:scale-110 active:scale-95"
                              style={{
                                color: myLikes.has(e.participant_id) ? '#ef4444' : 'rgba(255,255,255,0.5)',
                              }}
                            >
                              <Heart size={12} fill={myLikes.has(e.participant_id) ? '#ef4444' : 'none'} stroke={myLikes.has(e.participant_id) ? '#ef4444' : 'currentColor'} />
                              {(likeCounts[e.participant_id] || 0) > 0 && (likeCounts[e.participant_id])}
                            </button>
                          ) : (likeCounts[e.participant_id] || 0) > 0 ? (
                            <span className="flex items-center gap-1 text-[10px] text-white/40">
                              <Heart size={10} /> {likeCounts[e.participant_id]}
                            </span>
                          ) : null}
                          <div className="text-base font-bold" style={{ color: league.accent_color }}>
                            {Number(e.score).toFixed(1)}
                          </div>
                        </div>
                      </li>
                      );
                    })}
                  </ol>
                )}
                <div className="p-3 border-t border-white/10 text-center">
                  <Link
                    to={`/${basePath}/${slug}/leaderboard`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold hover:underline"
                    style={{ color: league.accent_color }}
                  >
                    View live leaderboard <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            </aside>
          </div>


        </div>

        {/* Powered by footer */}
        <footer className="px-4 py-6 border-t border-white/10 bg-black/40 backdrop-blur-md">
          <div className="max-w-5xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="text-sm uppercase tracking-[0.15em] text-white/80 font-bold">
                Powered by The Singing League
              </span>
              <img src="/TSL_Logo.png" alt="The Singing League" className="h-12 md:h-14 object-contain" />
            </div>
            {filteredSponsors.length > 0 && (
              <div className="flex items-center justify-center gap-8 md:gap-12 flex-wrap">
                {filteredSponsors.map((logo, i) => {
                  const link = filteredSponsorLinks[i];
                  const img = (
                    <img
                      src={logo}
                      alt={`Sponsor ${i + 1}`}
                      className="h-12 md:h-14 object-contain opacity-90 hover:opacity-100 transition"
                    />
                  );
                  return link ? (
                    <a key={i} href={link} target="_blank" rel="noreferrer">{img}</a>
                  ) : (
                    <div key={i}>{img}</div>
                  );
                })}
              </div>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
};

export default KioskLeagueLanding;
